import { useState, useRef, useCallback } from "react";
import { GPS_SCHOOLS, ME_SCHOOLS, computeDerived, ESA_NAMES } from "./schoolData";
import { auth } from "../config/firebase";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_DATA    = "'Rajdhani', 'Trebuchet MS', sans-serif";
const FONT_BODY    = "'EB Garamond', Georgia, serif";

const COLORS = {
  ink:    "#1a1714",
  cream:  "#faf7f2",
  gold:   "#b8924a",
  navy:   "#1a3a5c",
  muted:  "#7a7068",
  border: "#e8e2d8",
  bg:     "#f4f0e8",
  green:  "#22863a",
  amber:  "#b8924a",
  red:    "#8b2c2c",
};

const CONF_COLOR = { V: COLORS.green, E: COLORS.amber, L: COLORS.red };
const CONF_LABEL = {
  V: "Verified",
  E: "Estimated",
  L: "Low confidence — do not cite without independent verification",
};

// ─── PROXY URL ────────────────────────────────────────────────────────────────
// Replace with your actual Cloud Function URL after deploy
const PROXY_URL = "https://us-central1-kings-app-74847.cloudfunctions.net/anthropicProxy";

// ─── SYSTEM PROMPT (built dynamically from live school data) ─────────────────
function buildSystemPrompt() {
  const allSchools = computeDerived([
    ...GPS_SCHOOLS,
    ...ME_SCHOOLS.filter(s => !s.isKings),
  ]);

  // Serialize each school to a compact line Claude can read
  const fmt = (field) => field?.val !== null && field?.val !== undefined
    ? `${field.val}${field.conf !== "V" ? `(${field.conf})` : ""}`
    : "—";

  const schoolLines = allSchools.map(s => {
    const tags = [
      s.isKings ? "KINGS" : null,
      ESA_NAMES.has(s.name) ? "ESA" : null,
      s.isME ? "ME" : null,
    ].filter(Boolean).join(",");
    return `${s.name}|${s.state}|${tags}|boardT:${fmt(s.boardingT)}|dayT:${fmt(s.dayT)}|aid%:${fmt(s.aidPct)}|aidBudget:${fmt(s.aidBudgetM)}M|endow:${s.endowmentText}|board%:${fmt(s.boardingPct)}|enroll:${fmt(s.enrollment)}`;
  }).join("\n");

  return `You are a data visualization assistant for King's Academy's internal benchmarking tool.
You respond ONLY with a valid JSON object — no preamble, no markdown fences, no text outside the JSON.

You have TWO response modes. Choose based on what the user is asking:

MODE 1 — Conversational reply. Use this when the user asks a question, wants an explanation,
asks what something means, or is not explicitly requesting a new visualization.
{ "type": "message", "text": string }

MODE 2 — Chart. Use this when the user explicitly asks for a chart, graph, comparison,
or uses words like "show me", "visualize", "chart", "plot", "compare".
{
  "type": "chart",
  "chartType": "bar" | "horizontalBar" | "line" | "scatter",
  "title": string,
  "subtitle": string,
  "xLabel": string,
  "yLabel": string,
  "unit": "$" | "%" | "x" | "M" | "",
  "unitPosition": "prefix" | "suffix",
  "data": [
    { "label": string, "value": number, "isKings": boolean, "confidence": "V" | "E" | "L", "confidenceNote": string }
  ],
  "confidenceReport": {
    "summary": string, "verifiedCount": number, "estimatedCount": number,
    "lowConfidenceCount": number, "sources": string[], "caveats": string[]
  },
  "insight": string
}

Rules:
- Default to MODE 1 for follow-up questions, clarifications, and explanations. Keep replies to 2-4 sentences.
- Only use MODE 2 when a new visualization is clearly requested.
- Always include King's Academy in the dataset where relevant, with isKings: true.
- Include ALL relevant schools when asked — never truncate the dataset. If the user asks for all schools, all ME schools, all GPS schools, etc., return every matching entry from the data. Prefer horizontalBar for school comparisons as it handles large lists cleanly.
- ME schools are all day schools — use dayT for comparisons with them, not boardT.
- ESA = Eight Schools Association: Andover, Exeter, Choate, Deerfield, Hotchkiss, Lawrenceville, NMH, St. Paul's.
- Confidence: V = verified primary source, E = estimated/secondary, L = low confidence.
- Return ONLY the JSON. No other text.

SCHOOL DATA (pipe-delimited, live from database):
Format: Name|State/Region|Tags|boardT:USD|dayT:USD|aid%|aidBudget|endowment|board%|enrollment
Tags: KINGS=King's Academy, ESA=Eight Schools Association, ME=Middle East school
Values in parentheses show confidence: (E)=estimated, (L)=low confidence; no parens=Verified.
All tuition in USD. ME school dayT figures are G11-12 max where verified.

${schoolLines}`;
}

const SYSTEM_PROMPT = buildSystemPrompt();

// ─── EXAMPLE PROMPTS ─────────────────────────────────────────────────────────
const EXAMPLES = [
  "Compare endowments of the ESA schools",
  "Show financial aid % for schools with over 400 students",
  "How does King's tuition compare to Deerfield, Exeter, and Andover?",
  "Chart the sustainability ratio (aid budget vs endowment) for top peer schools",
  "Compare boarding percentages across all ESA schools and King's",
];

const REFINE_SUGGESTIONS = [
  "Narrow to ESA schools only",
  "Add King's Academy to the comparison",
  "Show only schools with over 500 students",
  "Sort ascending instead",
];

// ─── BAR CHART ────────────────────────────────────────────────────────────────
function BarChart({ data, unit, unitPosition, isHorizontal, highlight }) {
  const maxVal = Math.max(...data.map(d => d.value));
  const fmt = v => {
    if (unit === "M") return `$${v}M`;
    if (unit === "$") return `$${v >= 1000 ? (v / 1000).toFixed(0) + "K" : v}`;
    const s = unitPosition === "prefix" ? unit : "";
    const e = unitPosition === "suffix" ? unit : "";
    return `${s}${typeof v === "number" && v % 1 !== 0 ? v.toFixed(1) : v.toLocaleString()}${e}`;
  };

  if (isHorizontal) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
        {[...data].sort((a, b) => a.value - b.value).map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 160, fontSize: 12, fontFamily: FONT_DATA, color: d.isKings ? COLORS.navy : COLORS.ink, fontWeight: d.isKings ? 700 : 400, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {d.isKings ? "★ " : ""}{d.label}
            </div>
            <div style={{ flex: 1, background: COLORS.border, borderRadius: 2, height: 22, position: "relative", overflow: "hidden" }}>
              <div style={{ width: `${(d.value / maxVal) * 100}%`, height: "100%", background: d.isKings ? COLORS.navy : highlight ? CONF_COLOR[d.confidence] : "#a09080", borderRadius: 2, opacity: d.isKings ? 1 : 0.7, transition: "width 0.6s cubic-bezier(0.16,1,0.3,1)" }} />
            </div>
            <div style={{ width: 70, fontSize: 12, fontFamily: FONT_DATA, fontWeight: 600, color: d.isKings ? COLORS.navy : COLORS.ink, textAlign: "right", flexShrink: 0 }}>{fmt(d.value)}</div>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: CONF_COLOR[d.confidence], flexShrink: 0 }} title={CONF_LABEL[d.confidence]} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 220, width: "100%", paddingBottom: 28, position: "relative" }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end", gap: 4 }}>
          <div style={{ fontSize: 10, fontFamily: FONT_DATA, fontWeight: 600, color: d.isKings ? COLORS.navy : COLORS.ink }}>{fmt(d.value)}</div>
          <div style={{ width: "100%", height: `${(d.value / maxVal) * 170}px`, background: d.isKings ? COLORS.navy : highlight ? CONF_COLOR[d.confidence] : "#a09080", borderRadius: "2px 2px 0 0", opacity: d.isKings ? 1 : 0.7, transition: "height 0.6s cubic-bezier(0.16,1,0.3,1)", position: "relative" }}>
            <div style={{ position: "absolute", top: -4, right: -4, width: 8, height: 8, borderRadius: "50%", background: CONF_COLOR[d.confidence], border: "1px solid white" }} />
          </div>
          <div style={{ fontSize: 9, fontFamily: FONT_DATA, color: d.isKings ? COLORS.navy : COLORS.muted, textAlign: "center", fontWeight: d.isKings ? 700 : 400, lineHeight: 1.2, maxWidth: "100%" }}>
            {d.isKings ? "★ " : ""}{d.label.replace(/^The /, "").replace(/ (School|Academy)$/, "")}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── CONFIDENCE REPORT ────────────────────────────────────────────────────────
function ConfidenceReport({ report, data }) {
  const total = data.length;
  const vPct = Math.round((report.verifiedCount / total) * 100);
  const ePct = Math.round((report.estimatedCount / total) * 100);
  const lPct = Math.round((report.lowConfidenceCount / total) * 100);
  return (
    <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.ink, lineHeight: 1.7 }}>
      <p style={{ fontStyle: "italic", color: COLORS.muted, marginTop: 0 }}>{report.summary}</p>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 6 }}>Data confidence breakdown</div>
        <div style={{ display: "flex", height: 12, borderRadius: 2, overflow: "hidden", marginBottom: 6 }}>
          {vPct > 0 && <div style={{ width: `${vPct}%`, background: COLORS.green }} />}
          {ePct > 0 && <div style={{ width: `${ePct}%`, background: COLORS.amber }} />}
          {lPct > 0 && <div style={{ width: `${lPct}%`, background: COLORS.red }} />}
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[{ label: `Verified (${report.verifiedCount})`, color: COLORS.green }, { label: `Estimated (${report.estimatedCount})`, color: COLORS.amber }, { label: `Low confidence (${report.lowConfidenceCount})`, color: COLORS.red }].map(({ label, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: FONT_DATA, fontSize: 11, color: COLORS.muted }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />{label}
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 8 }}>Per-school confidence</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {data.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: CONF_COLOR[d.confidence], flexShrink: 0, marginTop: 3 }} />
              <div>
                <span style={{ fontFamily: FONT_DATA, fontWeight: 600, fontSize: 12, color: d.isKings ? COLORS.navy : COLORS.ink }}>{d.isKings ? "★ " : ""}{d.label}</span>
                <span style={{ fontFamily: FONT_DATA, fontSize: 11, color: COLORS.muted, marginLeft: 6 }}>{CONF_LABEL[d.confidence]} — {d.confidenceNote}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {report.sources?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 4 }}>Sources consulted</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: COLORS.muted, fontSize: 12, fontFamily: FONT_DATA }}>
            {report.sources.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {report.caveats?.length > 0 && (
        <div style={{ background: "#fff8ed", border: `1px solid ${COLORS.amber}`, borderRadius: 2, padding: "10px 14px" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.amber, marginBottom: 6 }}>⚠ Caveats</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: COLORS.ink, fontSize: 12, fontFamily: FONT_BODY, lineHeight: 1.65 }}>
            {report.caveats.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── SINGLE CHART CARD ────────────────────────────────────────────────────────
function ChartCard({ chartData, index, total, highlight, onHighlightToggle, onDownloadSingle, pdfLoading, chartRef }) {
  const [tab, setTab] = useState("chart");
  const isHoriz = chartData.chartType === "horizontalBar" || chartData.data?.length > 6;

  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 24 }}>

      {/* Card header */}
      <div style={{ padding: "14px 22px 12px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, background: COLORS.bg }}>
        <div>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 3 }}>
            Chart {index + 1} of {total}
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.1rem", fontWeight: 600, margin: "0 0 3px" }}>{chartData.title}</h2>
          {chartData.subtitle && <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: COLORS.muted, fontSize: 13, margin: 0 }}>{chartData.subtitle}</p>}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={onHighlightToggle}
            style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${highlight ? COLORS.amber : COLORS.border}`, borderRadius: 2, background: highlight ? "#fff8ed" : COLORS.cream, color: highlight ? COLORS.amber : COLORS.muted }}>
            {highlight ? "✕ Conf. Color" : "Color by Conf."}
          </button>
          <button onClick={() => onDownloadSingle(index)} disabled={pdfLoading}
            style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${COLORS.navy}`, borderRadius: 2, background: pdfLoading ? COLORS.muted : COLORS.navy, color: COLORS.cream, opacity: pdfLoading ? 0.7 : 1 }}>
            ↓ This Chart
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.border}` }}>
        {["chart", "report"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: 1, padding: "9px 0", fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", border: "none", background: tab === t ? COLORS.cream : "#fff", color: tab === t ? COLORS.ink : COLORS.muted, borderBottom: tab === t ? `2px solid ${COLORS.ink}` : "none" }}>
            {t === "chart" ? "Chart" : "Confidence Report"}
          </button>
        ))}
      </div>

      {/* Chart tab */}
      {tab === "chart" && (
        <div ref={chartRef} style={{ padding: "22px 26px", background: "#fff" }}>
          {chartData.insight && (
            <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${COLORS.gold}`, borderRadius: 2, padding: "10px 14px", marginBottom: 18, fontFamily: FONT_BODY, fontStyle: "italic", fontSize: 13, color: COLORS.ink, lineHeight: 1.6 }}>
              <strong style={{ fontFamily: FONT_DATA, fontStyle: "normal", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.gold, display: "block", marginBottom: 4 }}>Key Insight</strong>
              {chartData.insight}
            </div>
          )}
          <BarChart data={chartData.data} unit={chartData.unit} unitPosition={chartData.unitPosition} isHorizontal={isHoriz} highlight={highlight} />
          <div style={{ display: "flex", gap: 16, marginTop: 14, paddingTop: 10, borderTop: `1px solid ${COLORS.border}`, flexWrap: "wrap" }}>
            {[["V", "Verified", COLORS.green], ["E", "Estimated", COLORS.amber], ["L", "Low confidence", COLORS.red]].map(([k, label, color]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: FONT_DATA, fontSize: 11, color: COLORS.muted }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />{label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report tab */}
      {tab === "report" && (
        <div style={{ padding: "22px 26px" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 14 }}>
            Confidence Report · {chartData.title}
          </div>
          <ConfidenceReport report={chartData.confidenceReport} data={chartData.data} />
        </div>
      )}
    </div>
  );
}

// ─── PDF BUILDER (shared between single + all) ────────────────────────────────
async function buildChartPages(doc, W, H, chartEntry, chartNode, chartIndex, totalCharts) {
  const NAVY = [26, 58, 92], GOLD = [184, 146, 74], INK = [26, 23, 20];
  const MUTED = [122, 112, 104], GRN = [34, 134, 58], AMB = [184, 146, 74], RED = [139, 44, 44];
  const confColor = c => c === "V" ? GRN : c === "E" ? AMB : RED;
  const confLabel = c => c === "V" ? "Verified" : c === "E" ? "Estimated" : "Low confidence";
  const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  // ── Chart page ──
  doc.setFillColor(...NAVY); doc.rect(0, 0, W, 36, "F");
  doc.setFillColor(...GOLD); doc.rect(0, 36, W, 0.8, "F");
  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text("KING'S ACADEMY  ·  INTERNAL REFERENCE", W / 2, 10, { align: "center" });
  if (totalCharts > 1) {
    doc.text(`Chart ${chartIndex + 1} of ${totalCharts}`, W - 14, 10, { align: "right" });
  }
  doc.setTextColor(250, 247, 242); doc.setFontSize(15); doc.setFont("helvetica", "bold");
  doc.text(chartEntry.title, W / 2, 21, { align: "center", maxWidth: W - 24 });
  if (chartEntry.subtitle) {
    doc.setFontSize(9); doc.setFont("helvetica", "italic"); doc.setTextColor(200, 191, 176);
    doc.text(chartEntry.subtitle, W / 2, 30, { align: "center" });
  }
  doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
  doc.text(`Generated ${now}  ·  Query: "${chartEntry._query}"`, W / 2, 42, { align: "center", maxWidth: W - 24 });

  if (chartEntry.insight) {
    doc.setFillColor(244, 240, 232); doc.setDrawColor(...GOLD);
    doc.roundedRect(12, 47, W - 24, 14, 1, 1, "FD");
    doc.setFillColor(...GOLD); doc.rect(12, 47, 2, 14, "F");
    doc.setTextColor(...INK); doc.setFontSize(8); doc.setFont("helvetica", "bolditalic");
    doc.text(doc.splitTextToSize(chartEntry.insight, W - 36), 18, 53);
  }

  // Load html2canvas via script tag (CRA can't handle dynamic CDN imports)
  if (!window.html2canvas) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const html2canvas = window.html2canvas;
  if (!html2canvas) throw new Error("html2canvas failed to load");
  const canvas = await html2canvas(chartNode, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });

  // Render chart across multiple pages if taller than available space
  const imgW = W - 24;
  const imgH = imgW / (canvas.width / canvas.height); // natural height at page width
  const startY = 65;           // top of first chart area (below header/insight)
  const availFirst = H - startY - 10;  // space on first page
  const availRest  = H - 20;           // space on subsequent pages

  if (imgH <= availFirst) {
    // Fits on first page — render normally
    doc.addImage(canvas.toDataURL("image/png"), "PNG", 12, startY, imgW, imgH);
  } else {
    // Tall chart — slice across pages. Instead of guessing row height, we scan the canvas
    // pixel data to find the best cut point: the nearest row that is predominantly white
    // (inter-row gap) at or before the page boundary. This guarantees cuts never bisect
    // a school name or bar regardless of actual row height.
    const pxPerMm = canvas.width / imgW;
    const ctx2 = document.createElement("canvas").getContext("2d");
    ctx2.canvas.width = canvas.width; ctx2.canvas.height = canvas.height;
    ctx2.drawImage(canvas, 0, 0);
    const px = ctx2.getImageData(0, 0, canvas.width, canvas.height).data;
    const W_px = canvas.width;

    // Returns true if a horizontal scan line at pixelY is mostly white/near-white
    const isWhiteRow = (pixelY) => {
      if (pixelY < 0 || pixelY >= canvas.height) return true;
      const sampleStep = Math.max(1, Math.floor(W_px / 40)); // sample 40 points across
      let lightCount = 0, total = 0;
      for (let x = 0; x < W_px; x += sampleStep) {
        const i = (pixelY * W_px + x) * 4;
        const brightness = (px[i] + px[i+1] + px[i+2]) / 3;
        if (brightness > 230) lightCount++;
        total++;
      }
      return lightCount / total > 0.85; // >85% light pixels = whitespace row
    };

    // Find the best cut point: scan upward from maxSrcY to find a white row
    const findCutY = (fromSrcY, maxSrcH) => {
      const ideal = fromSrcY + maxSrcH;
      if (ideal >= canvas.height) return canvas.height - fromSrcY;
      // Scan up to 60px back to find a white gap
      for (let y = ideal; y >= ideal - 60 && y > fromSrcY; y--) {
        if (isWhiteRow(y)) return y - fromSrcY;
      }
      // No white row found — fall back to hard cut at ideal
      return maxSrcH;
    };

    let srcY = 0;
    let destY = startY;
    let availH = availFirst;

    while (srcY < canvas.height) {
      const maxSrcH = Math.min(availH * pxPerMm, canvas.height - srcY);
      const srcH = findCutY(srcY, maxSrcH);
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width  = canvas.width;
      sliceCanvas.height = srcH;
      sliceCanvas.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
      const sliceH = srcH / pxPerMm;
      doc.addImage(sliceCanvas.toDataURL("image/png"), "PNG", 12, destY, imgW, sliceH);
      srcY += srcH;
      if (srcY < canvas.height) {
        doc.addPage();
        // Continuation header
        doc.setFillColor(...NAVY); doc.rect(0, 0, W, 12, "F");
        doc.setFillColor(...GOLD); doc.rect(0, 12, W, 0.5, "F");
        doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
        doc.text(`${chartEntry.title}  (continued)`, W / 2, 8, { align: "center" });
        destY = 18;
        availH = availRest - 18;
      }
    }
  }

  // ── Confidence report page ──
  doc.addPage();
  doc.setFillColor(...NAVY); doc.rect(0, 0, W, 20, "F");
  doc.setFillColor(...GOLD); doc.rect(0, 20, W, 0.6, "F");
  doc.setTextColor(250, 247, 242); doc.setFontSize(13); doc.setFont("helvetica", "bold");
  doc.text("Confidence Report", 14, 13);
  doc.setTextColor(212, 172, 106); doc.setFontSize(7.5);
  doc.text(chartEntry.title, W - 14, 13, { align: "right" });

  let y = 30;
  const line = (txt, opts = {}) => {
    const { size = 8.5, font = "normal", color = INK, indent = 0 } = opts;
    doc.setFontSize(size); doc.setFont("helvetica", font); doc.setTextColor(...color);
    const lines = doc.splitTextToSize(txt, W - 28 - indent);
    doc.text(lines, 14 + indent, y);
    y += lines.length * (size * 0.45) + 2;
  };
  const section = t => {
    y += 3;
    doc.setFillColor(244, 240, 232); doc.rect(12, y - 4, W - 24, 8, "F");
    doc.setFillColor(...GOLD); doc.rect(12, y - 4, 2, 8, "F");
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...INK);
    doc.text(t.toUpperCase(), 17, y + 0.5); y += 7;
  };

  section("Summary");
  line(chartEntry.confidenceReport.summary, { font: "italic", color: MUTED });
  y += 2;

  const { verifiedCount: vC, estimatedCount: eC, lowConfidenceCount: lC } = chartEntry.confidenceReport;
  const tot = chartEntry.data.length;
  const bW = W - 28;
  doc.setFillColor(...GRN); doc.rect(14, y, (vC / tot) * bW, 5, "F");
  doc.setFillColor(...AMB); doc.rect(14 + (vC / tot) * bW, y, (eC / tot) * bW, 5, "F");
  doc.setFillColor(...RED); doc.rect(14 + (vC / tot) * bW + (eC / tot) * bW, y, (lC / tot) * bW, 5, "F");
  y += 8;
  [[GRN, `Verified — ${vC} of ${tot}`], [AMB, `Estimated — ${eC} of ${tot}`], [RED, `Low confidence — ${lC} of ${tot}`]].forEach(([color, label], i) => {
    doc.setFillColor(...color); doc.circle(14 + i * 60 + 2, y - 1, 1.5, "F");
    doc.setTextColor(...MUTED); doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.text(label, 14 + i * 60 + 5.5, y);
  });
  y += 6;

  section("Per-School Confidence");
  chartEntry.data.forEach(d => {
    if (y > H - 20) { doc.addPage(); y = 20; }
    doc.setFillColor(...confColor(d.confidence)); doc.circle(16, y - 1, 1.5, "F");
    doc.setFontSize(8); doc.setFont("helvetica", d.isKings ? "bold" : "normal");
    doc.setTextColor(...(d.isKings ? NAVY : INK));
    doc.text(`${d.isKings ? "★ " : "  "}${d.label}`, 20, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED); doc.setFontSize(7.5);
    doc.text(`${confLabel(d.confidence)}  —  ${d.confidenceNote}`, 20, y + 3.5);
    y += 9;
  });

  if (chartEntry.confidenceReport.sources?.length) {
    section("Sources Consulted");
    chartEntry.confidenceReport.sources.forEach(s => line(`· ${s}`, { color: MUTED }));
  }

  if (chartEntry.confidenceReport.caveats?.length) {
    y += 2;
    const cavH = chartEntry.confidenceReport.caveats.length * 10 + 14;
    if (y + cavH > H - 12) { doc.addPage(); y = 20; }
    doc.setFillColor(255, 248, 237); doc.setDrawColor(...AMB);
    doc.roundedRect(12, y - 2, W - 24, cavH, 1, 1, "FD");
    doc.setFillColor(...AMB); doc.rect(12, y - 2, 2, cavH, "F");
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...AMB);
    doc.text("⚠  CAVEATS", 18, y + 4); y += 9;
    chartEntry.confidenceReport.caveats.forEach(c => line(`· ${c}`, { color: INK, indent: 4 }));
  }
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AIChartGenerator() {
  const [charts, setCharts]         = useState([]);
  const [history, setHistory]       = useState([]); // [{role, content}] full Claude conversation
  const [input, setInput]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError]           = useState(null);
  const [highlights, setHighlights] = useState({});
  const [showThread, setShowThread] = useState(false);

  const inputRef  = useRef(null);
  const chartRefs = useRef([]);

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = useCallback(async (override) => {
    const msg = (override || input).trim();
    if (!msg || loading) return;

    const newHistory = [...history, { role: "user", content: msg }];
    setHistory(newHistory);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      // Get Firebase ID token to authenticate with the proxy
      const idToken = await auth.currentUser.getIdToken();

      const res = await fetch(PROXY_URL, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          model:      "claude-sonnet-4-6",
          max_tokens: 8000,
          system:     SYSTEM_PROMPT,
          messages:   newHistory.map(({ role, content }) => ({ role, content })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `API error ${res.status}`);
      }

      const raw    = await res.json();
      const text   = raw.content?.map(b => b.text || "").join("") || "";
      const clean  = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);

      if (parsed.type === "message") {
        setHistory(prev => [...prev, { role: "assistant", content: text, _parsed: parsed }]);
        setShowThread(true); // show the reply automatically
      } else {
        setCharts(prev => [...prev, { ...parsed, _query: msg }]);
        setHistory(prev => [...prev, { role: "assistant", content: text, _parsed: parsed }]);
      }
    } catch (e) {
      setError(e.message || "Could not parse a chart. Try rephrasing your question.");
      setHistory(history); // rollback
    } finally {
      setLoading(false);
    }
  }, [input, history, loading]);

  // ── Load jsPDF via script tag (CRA webpack can't handle dynamic CDN imports) ─
  const getJSPDF = async () => {
    if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
    const JSPDF = window.jspdf?.jsPDF;
    if (!JSPDF) throw new Error("jsPDF failed to load");
    return JSPDF;
  };

  // ── Load html2canvas via script tag ──────────────────────────────────────────
  const getHtml2Canvas = async () => {
    if (window.html2canvas) return window.html2canvas;
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
    if (!window.html2canvas) throw new Error("html2canvas failed to load");
    return window.html2canvas;
  };

  // ── Download single chart ────────────────────────────────────────────────────
  const handleDownloadSingle = useCallback(async (idx) => {
    const node = chartRefs.current[idx];
    const entry = charts[idx];
    if (!node || !entry) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      await buildChartPages(doc, W, H, entry, node, idx, charts.length);
      // Footers
      const pc = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pc; i++) {
        doc.setPage(i);
        doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
        doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(122, 112, 104);
        doc.text("Internal use only · King's Academy Peer Benchmarking", W / 2, H - 4, { align: "center" });
        doc.text(`${i} / ${pc}`, W - 14, H - 4);
      }
      doc.save(`KingsAcademy_${entry.title.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "")}.pdf`);
    } catch (e) {
      console.error("PDF error:", e);
      setError("PDF generation failed. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  }, [charts]);

  // ── Download all charts as combined report ────────────────────────────────────
  const handleDownloadAll = useCallback(async () => {
    if (charts.length === 0) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const NAVY = [26, 58, 92], GOLD = [184, 146, 74], INK = [26, 23, 20], MUTED = [122, 112, 104];
      const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

      // ── Cover page ──
      doc.setFillColor(...NAVY); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(...GOLD); doc.rect(20, H / 2 - 2, W - 40, 1.5, "F");
      doc.setTextColor(212, 172, 106); doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.text("KING'S ACADEMY  ·  INTERNAL REFERENCE", W / 2, H / 2 - 32, { align: "center" });
      doc.setTextColor(250, 247, 242); doc.setFontSize(22); doc.setFont("helvetica", "bold");
      doc.text("Peer School Benchmarking", W / 2, H / 2 - 16, { align: "center" });
      doc.setFontSize(13); doc.setFont("helvetica", "italic"); doc.setTextColor(200, 191, 176);
      doc.text("AI-Generated Chart Report", W / 2, H / 2 - 6, { align: "center" });
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(122, 112, 104);
      doc.text(`${charts.length} chart${charts.length > 1 ? "s" : ""} · ${now}`, W / 2, H / 2 + 14, { align: "center" });

      // ── Each chart ──
      for (let i = 0; i < charts.length; i++) {
        const node = chartRefs.current[i];
        if (!node) continue;
        doc.addPage();
        await buildChartPages(doc, W, H, charts[i], node, i, charts.length);
      }

      // ── Conversation appendix ──
      // Filter to user questions and Claude text replies only
      const convoEntries = history.filter(msg => {
        if (msg.role === "user") return true;
        if (msg.role === "assistant" && msg._parsed?.type === "message") return true;
        return false;
      });

      if (convoEntries.length > 0) {
        doc.addPage();

        // Appendix header band
        doc.setFillColor(...NAVY); doc.rect(0, 0, W, 24, "F");
        doc.setFillColor(...GOLD); doc.rect(0, 24, W, 0.8, "F");
        doc.setTextColor(250, 247, 242); doc.setFontSize(14); doc.setFont("helvetica", "bold");
        doc.text("Conversation", 14, 15);
        doc.setTextColor(212, 172, 106); doc.setFontSize(8);
        doc.text("Questions asked and responses during this session", W - 14, 15, { align: "right" });

        let y = 34;

        convoEntries.forEach((msg, i) => {
          const isUser = msg.role === "user";
          const msgText = isUser ? msg.content : msg._parsed.text;

          // Check page overflow — leave room for at least one line
          if (y > H - 30) {
            doc.addPage();
            y = 20;
          }

          // Role label with coloured left rule
          const ruleColor = isUser ? NAVY : GOLD;
          const label = isUser ? "Question" : "Response";

          // Measure text first to size the block
          doc.setFontSize(8.5);
          doc.setFont("helvetica", "normal");
          const lines = doc.splitTextToSize(msgText, W - 36);
          const blockH = lines.length * 4.2 + 14;

          // Check if whole block fits, otherwise page break
          if (y + blockH > H - 16) {
            doc.addPage();
            y = 20;
          }

          // Background block
          doc.setFillColor(isUser ? 232 : 244, isUser ? 240 : 240, isUser ? 248 : 232);
          doc.rect(12, y, W - 24, blockH, "F");

          // Left rule
          doc.setFillColor(...ruleColor);
          doc.rect(12, y, 2.5, blockH, "F");

          // Role label
          doc.setFontSize(7); doc.setFont("helvetica", "bold");
          doc.setTextColor(...ruleColor);
          doc.text(label.toUpperCase(), 18, y + 5.5);

          // Message text
          doc.setFontSize(8.5); doc.setFont("helvetica", "normal");
          doc.setTextColor(...INK);
          doc.text(lines, 18, y + 11);

          y += blockH + 5;
        });
      }

      // ── Footers on all pages except cover ──
      const pc = doc.internal.getNumberOfPages();
      for (let i = 2; i <= pc; i++) {
        doc.setPage(i);
        doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
        doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED);
        doc.text("Internal use only · King's Academy Peer Benchmarking", W / 2, H - 4, { align: "center" });
        doc.text(`${i - 1} / ${pc - 1}`, W - 14, H - 4);
      }

      doc.save(`KingsAcademy_PeerReport_${now.replace(/,?\s+/g, "_")}.pdf`);
    } catch (e) {
      console.error("PDF error:", e);
      setError("Report generation failed. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  }, [charts, history]);

  const hasCharts = charts.length > 0;

  return (
    <div style={{ fontFamily: FONT_BODY, background: COLORS.cream, minHeight: "100vh", color: COLORS.ink }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        button { cursor: pointer; }
        textarea:focus { outline: 2px solid ${COLORS.navy}; outline-offset: 1px; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #c8bfb0; border-radius: 3px; }
      `}</style>

      {/* ── HEADER ── */}
      <header style={{ background: COLORS.ink, padding: "2rem 2rem 1.6rem", borderBottom: `3px solid ${COLORS.gold}` }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: COLORS.gold, textTransform: "uppercase", marginBottom: 8 }}>
              King's Academy · Internal Tool
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(1.5rem,3vw,2.2rem)", fontWeight: 700, color: COLORS.cream, margin: "0 0 6px", lineHeight: 1.15 }}>
              AI Chart <em style={{ color: COLORS.gold }}>Generator</em>
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: "rgba(250,247,242,0.55)", fontSize: 14, margin: 0 }}>
              Ask questions · refine through conversation · download individual charts or a full report.
            </p>
          </div>
          {hasCharts && (
            <button onClick={handleDownloadAll} disabled={pdfLoading}
              style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", padding: "10px 20px", background: pdfLoading ? COLORS.muted : COLORS.gold, color: COLORS.ink, border: "none", borderRadius: 2, opacity: pdfLoading ? 0.7 : 1, whiteSpace: "nowrap", flexShrink: 0 }}>
              {pdfLoading ? "Building…" : `↓ Full Report (${charts.length} chart${charts.length > 1 ? "s" : ""})`}
            </button>
          )}
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: "0 auto", padding: "2rem" }}>

        {/* ── CHART STACK ── */}
        {charts.map((chartData, idx) => (
          <ChartCard
            key={idx}
            chartData={chartData}
            index={idx}
            total={charts.length}
            highlight={!!highlights[idx]}
            onHighlightToggle={() => setHighlights(h => ({ ...h, [idx]: !h[idx] }))}
            onDownloadSingle={handleDownloadSingle}
            pdfLoading={pdfLoading}
            chartRef={el => { chartRefs.current[idx] = el; }}
          />
        ))}

        {/* Loading */}
        {loading && (
          <div style={{ background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 3, padding: 32, textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.1rem", color: COLORS.muted, fontStyle: "italic", marginBottom: 8 }}>Generating chart…</div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: "#c8bfb0" }}>Querying Claude · parsing data · rendering</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: "#fff5f5", border: `1px solid ${COLORS.red}`, borderRadius: 2, padding: "12px 16px", marginBottom: 20, fontFamily: FONT_DATA, fontSize: 13, color: COLORS.red }}>
            {error}
          </div>
        )}

        {/* ── DIALOGUE BOX ── */}
        <div style={{ background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>

          {/* Dialogue header */}
          <div style={{ background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: COLORS.muted }}>
                {hasCharts ? "Refine or ask a new question" : "Ask about peer school data"}
              </div>
              {hasCharts && (
                <div style={{ fontFamily: FONT_BODY, fontStyle: "italic", fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                  Each response adds a new chart to the session above
                </div>
              )}
            </div>
            {hasCharts && (
              <button onClick={() => setShowThread(s => !s)}
                style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 14px", border: `1px solid ${showThread ? COLORS.navy : COLORS.border}`, borderRadius: 2, background: showThread ? "#e8f0f8" : COLORS.cream, color: showThread ? COLORS.navy : COLORS.muted, flexShrink: 0 }}>
                {showThread ? "▲ Hide Conversation" : "▼ View Conversation"}
              </button>
            )}
          </div>

          {/* Collapsible conversation thread */}
          {showThread && history.length > 0 && (
            <div style={{ borderBottom: `1px solid ${COLORS.border}`, maxHeight: 300, overflowY: "auto", padding: "16px 20px", background: "#fdfcf8", display: "flex", flexDirection: "column", gap: 12 }}>
              {history.map((msg, i) => {
                const parsed = msg._parsed;
                const isMsg = parsed?.type === "message";
                return (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: msg.role === "user" ? COLORS.navy : COLORS.gold, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 700, color: "#fff" }}>
                      {msg.role === "user" ? "You" : "AI"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 3 }}>
                        {msg.role === "user" ? "You" : "Claude"}
                      </div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.ink, lineHeight: 1.6, background: msg.role === "user" ? COLORS.bg : "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 2, padding: "8px 12px" }}>
                        {msg.role === "user" && msg.content}
                        {msg.role === "assistant" && isMsg && (
                          <div>
                            <div style={{ marginBottom: 8 }}>{parsed.text}</div>
                            <button
                              onClick={() => {
                                // Ask Claude to now make a chart based on this exchange
                                setShowThread(false);
                                setInput("Please turn your last response into a chart.");
                              }}
                              style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", padding: "3px 10px", border: `1px solid ${COLORS.navy}`, borderRadius: 2, background: COLORS.cream, color: COLORS.navy, cursor: "pointer" }}>
                              + Add as Chart
                            </button>
                          </div>
                        )}
                        {msg.role === "assistant" && !isMsg && (
                          <em style={{ color: COLORS.muted }}>↳ Chart generated — see above</em>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: "16px 20px" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
              placeholder={hasCharts
                ? "Refine the last chart, or ask something entirely new… (⌘+Enter to send)"
                : "e.g. Compare endowments of ESA schools and King's Academy (⌘+Enter to send)"}
              rows={2}
              style={{ width: "100%", border: `1px solid ${COLORS.border}`, borderRadius: 2, padding: "10px 12px", fontFamily: FONT_BODY, fontSize: 14, color: COLORS.ink, background: COLORS.cream, resize: "none", lineHeight: 1.5, marginBottom: 10 }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(hasCharts ? REFINE_SUGGESTIONS : EXAMPLES.slice(0, 3)).map(ex => (
                  <button key={ex} onClick={() => handleSend(ex)}
                    style={{ fontFamily: FONT_DATA, fontSize: 11, padding: "4px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 10, background: COLORS.bg, color: COLORS.muted, transition: "all 0.15s" }}
                    onMouseEnter={e => { e.target.style.borderColor = COLORS.navy; e.target.style.color = COLORS.navy; }}
                    onMouseLeave={e => { e.target.style.borderColor = COLORS.border; e.target.style.color = COLORS.muted; }}>
                    {ex}
                  </button>
                ))}
              </div>
              <button onClick={() => handleSend()} disabled={loading || !input.trim()}
                style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", padding: "8px 22px", background: loading ? COLORS.muted : COLORS.navy, color: COLORS.cream, border: "none", borderRadius: 2, opacity: !input.trim() ? 0.5 : 1, transition: "background 0.15s", whiteSpace: "nowrap" }}>
                {loading ? "Generating…" : hasCharts ? "Send →" : "Generate Chart →"}
              </button>
            </div>
          </div>
        </div>

        {/* How it works — only before first chart */}
        {!hasCharts && !loading && (
          <div style={{ marginTop: 16, padding: "16px 20px", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 2 }}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: COLORS.muted, marginBottom: 8 }}>How it works</div>
            <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.muted, lineHeight: 1.7 }}>
              Ask any question about peer school data in plain English. Each response generates a chart with per-datapoint confidence ratings. Refine through conversation — each reply adds a new chart. Download any chart individually, or export the entire session as a multi-chart PDF report with a cover page and confidence report for each chart.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}